import {
  useGetSettingsQuery,
  usePatchSettingsMutation,
  useGetAlphaBscPositionsQuery,
  useAlphaBscAutoTickNowMutation,
  useAlphaBscSellMutation,
  useRefreshAlphaBscLiveMutation,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtPrice, fmtQty } from "@/lib/format";
import { TokenSparkline } from "@/components/domain/TokenSparkline";
import { toast } from "sonner";

/**
 * Alpha BSC Auto-strategy — Phase 3 dashboard.
 *
 * - Master switch row (Phase 2 + Phase 3 toggles)
 * - Tier configuration display (read-only — change via Settings)
 * - Live auto-managed open positions with tier_state badges
 * - "Tick now" button to force-run the 5-min cron immediately
 */
export default function AlphaBscAutoPage() {
  const settings = useGetSettingsQuery();
  const [patch, { isLoading: patching }] = usePatchSettingsMutation();
  const openPos = useGetAlphaBscPositionsQuery("open", { pollingInterval: 30000 });
  const [tickNow, { isLoading: ticking }] = useAlphaBscAutoTickNowMutation();
  const [sellMut, { isLoading: selling }] = useAlphaBscSellMutation();
  const [refreshLive, { isLoading: refreshing }] = useRefreshAlphaBscLiveMutation();

  async function onRefreshLive() {
    try {
      const r: any = await refreshLive().unwrap();
      const grads = r?.graduations_detected?.length ?? 0;
      toast.success(
        grads > 0
          ? `🎓 ${grads} graduation(s)! refreshed ${r?.refreshed}`
          : `Live refresh OK — ${r?.refreshed}/${r?.total_watched} tokens updated`
      );
      openPos.refetch();
    } catch (e: any) {
      toast.error(`refresh failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  const s = settings.data as any;
  const phase2On = s?.bsc_trading_enabled === true;
  const phase3On = s?.alpha_bsc_auto_enabled === true;
  const autoPositions = (openPos.data || []).filter((p: any) => p.auto_managed === 1);

  async function togglePhase2() {
    try {
      await patch({ bsc_trading_enabled: !phase2On } as any).unwrap();
      toast.success(`Phase 2 trading ${!phase2On ? "ENABLED" : "disabled"}`);
    } catch (e: any) {
      toast.error(`toggle failed: ${e?.data?.detail ?? "?"}`);
    }
  }
  async function togglePhase3() {
    try {
      await patch({ alpha_bsc_auto_enabled: !phase3On } as any).unwrap();
      toast.success(`Auto-strategy ${!phase3On ? "ENABLED" : "disabled"}`);
    } catch (e: any) {
      toast.error(`toggle failed: ${e?.data?.detail ?? "?"}`);
    }
  }
  async function onTick() {
    try {
      const r: any = await tickNow().unwrap();
      const buys = (r?.buy_results || []).filter((x: any) => x.action === "auto_buy").length;
      const exits = (r?.manage_results || []).flatMap((m: any) =>
        (m.actions || []).filter((a: any) => (a.action || "").includes("sell"))).length;
      const status = r?.status;
      if (status === "ok") {
        toast.success(`Tick OK — managed ${r.open_positions_managed}, ${buys} new buys, ${exits} exits`);
      } else if (status === "disabled") {
        toast.error("Auto-strategy is OFF — toggle it on first");
      } else if (status === "phase2_master_off") {
        toast.error("Phase 2 master is OFF — toggle bsc_trading first");
      } else {
        toast.error(`Tick status: ${status}`);
      }
    } catch (e: any) {
      toast.error(`tick failed: ${e?.data?.detail ?? "?"}`);
    }
  }
  async function onSell(posId: number, sym: string, pct: number) {
    if (!confirm(`Manually sell ${(pct*100).toFixed(0)}% of ${sym} (overrides auto-strategy)?`)) return;
    try {
      const r: any = await sellMut({ position_id: posId, pct_to_sell: pct }).unwrap();
      if (r.status === "ok") {
        toast.success(`SOLD — P&L ${r.realized_pnl_usd >= 0 ? "+" : ""}$${r.realized_pnl_usd.toFixed(4)}`);
      } else {
        toast.error(`sell failed: ${r.reason}`);
      }
    } catch (e: any) {
      toast.error(`sell error: ${e?.data?.detail ?? "?"}`);
    }
  }

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Auto-strategy</h1>
        <p className="text-sm text-muted">
          Phase 3: scans watchlist every 5 min, auto-buys score-{">"}={s?.alpha_bsc_auto_buy_min_score ?? 70}
          tokens, manages each through tier exits (+30% / +60% / trail), hard-stop (-25%),
          and dumps 100% on Binance spot graduation event. Both Phase 2 and Phase 3
          master switches must be ON for any auto-trading.
        </p>
      </header>

      {/* MASTER SWITCHES */}
      <Card>
        <CardHeader>
          <CardTitle>Master switches</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <SwitchRow
            label="Phase 2 — On-chain trading (master)"
            subtext="Gates ALL on-chain swaps. Required for any BSC trade, manual or auto."
            isOn={phase2On}
            onToggle={togglePhase2}
            disabled={patching}
            tone={phase2On ? "good" : "muted"}
          />
          <SwitchRow
            label="Phase 3 — Auto-strategy (this engine)"
            subtext="Enables the 5-min scan + auto-buy + tier-exit + graduation-sell loop."
            isOn={phase3On}
            onToggle={togglePhase3}
            disabled={patching || !phase2On}
            tone={phase3On && phase2On ? "good" : phase3On && !phase2On ? "warn" : "muted"}
            warning={phase3On && !phase2On ? "Phase 2 is off — auto-strategy can't fire" : ""}
          />
        </CardContent>
      </Card>

      {/* CONFIG SUMMARY */}
      <Card>
        <CardHeader className="flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle>Strategy config</CardTitle>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={onRefreshLive} disabled={refreshing}>
              {refreshing ? "Refreshing…" : "↻ Refresh live"}
            </Button>
            <Button size="sm" variant="ghost" onClick={onTick} disabled={ticking || !phase3On || !phase2On}>
              {ticking ? "Ticking…" : "Tick now"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {settings.isLoading && <Skeleton className="h-24 w-full" />}
          {s && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <ConfigItem label="Buy size" value={`$${s.alpha_bsc_auto_buy_size_usdt ?? 25}`} />
              <ConfigItem label="Min score" value={String(s.alpha_bsc_auto_buy_min_score ?? 70)} />
              <ConfigItem label="Max concurrent" value={String(s.alpha_bsc_auto_max_concurrent ?? 3)} />
              <ConfigItem label="Tick interval" value={`${s.alpha_bsc_auto_tick_interval_minutes ?? 5}m`} />
              <ConfigItem label="TP1" value={`+${((s.alpha_bsc_auto_tp1_pct ?? 0.30) * 100).toFixed(0)}% sell ${((s.alpha_bsc_auto_tp1_sell_fraction ?? 0.5)*100).toFixed(0)}%`} />
              <ConfigItem label="TP2" value={`+${((s.alpha_bsc_auto_tp2_pct ?? 0.60) * 100).toFixed(0)}% sell ${((s.alpha_bsc_auto_tp2_sell_fraction ?? 0.25)*100).toFixed(0)}%`} />
              <ConfigItem label="Trail" value={`${((s.alpha_bsc_auto_trail_pct ?? 0.20) * 100).toFixed(0)}% below HW`} />
              <ConfigItem label="Hard stop" value={`-${((s.alpha_bsc_auto_hard_stop_pct ?? 0.25) * 100).toFixed(0)}%`} tone="bad" />
            </div>
          )}
          <p className="text-xs text-muted mt-3">
            All config values are tunable in Settings → search "alpha_bsc_auto_*".
            Per-position thresholds are frozen at buy-time, so changing settings
            won't retroactively affect open positions.
          </p>
        </CardContent>
      </Card>

      {/* AUTO-MANAGED OPEN POSITIONS */}
      <Card>
        <CardHeader>
          <CardTitle>
            Auto-managed open positions{" "}
            <span className="text-sm text-muted font-normal">({autoPositions.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {openPos.isLoading && <Skeleton className="h-24 w-full" />}
          {!openPos.isLoading && autoPositions.length === 0 && (
            <div className="text-sm text-muted italic">
              No auto-managed positions yet. When a watchlist token's graduation
              score crosses {s?.alpha_bsc_auto_buy_min_score ?? 70} and the strategy is
              on, a new position will appear here.
            </div>
          )}
          {autoPositions.length > 0 && (
            <Table>
              <THead>
                <TR>
                  <TH>Symbol</TH>
                  <TH>State</TH>
                  <TH className="text-right">Size</TH>
                  <TH className="text-right">Qty</TH>
                  <TH className="text-right">Entry $</TH>
                  <TH className="text-right">Cur $</TH>
                  <TH className="text-right">Unrealized</TH>
                  <TH>Chart</TH>
                  <TH className="text-right">Targets</TH>
                  <TH className="text-center">Override</TH>
                </TR>
              </THead>
              <TBody>
                {autoPositions.map((p: any) => {
                  const tierTone = p.tier_state === "runner" ? "good"
                                 : p.tier_state === "post_tp1" ? "info"
                                 : "muted";
                  const tp1Px = p.entry_price_usd * (1 + (p.tp1_pct ?? 0.30));
                  const tp2Px = p.entry_price_usd * (1 + (p.tp2_pct ?? 0.60));
                  const stopPx = p.entry_price_usd * (1 - (p.hard_stop_pct ?? 0.25));
                  const pnlPct = p.unrealized_pnl_pct;
                  const pnlUsd = p.unrealized_pnl_usd;
                  const hasLive = pnlPct !== undefined && pnlPct !== null;
                  return (
                    <TR key={p.id}>
                      <TD className="font-mono font-semibold">{p.symbol}</TD>
                      <TD><Badge tone={tierTone as any}>{p.tier_state || "pre_tp1"}</Badge></TD>
                      <TD className="text-right tabular-nums">${Number(p.entry_size_usdt).toFixed(2)}</TD>
                      <TD className="text-right tabular-nums font-mono text-xs">{fmtQty(Number(p.entry_qty_token))}</TD>
                      <TD className="text-right tabular-nums font-mono text-xs">
                        {fmtPrice(Number(p.entry_price_usd))}
                      </TD>
                      <TD className="text-right tabular-nums font-mono text-xs">
                        {hasLive ? fmtPrice(Number(p.current_price_usd)) : <span className="text-muted">—</span>}
                      </TD>
                      <TD className={`text-right tabular-nums font-semibold text-xs ${
                        hasLive ? (pnlPct >= 0 ? "text-green-500" : "text-red-500") : "text-muted"
                      }`}>
                        {hasLive ? (
                          <div>
                            <div>{pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(2)}%</div>
                            <div className="text-[10px] opacity-80">{pnlUsd >= 0 ? "+" : ""}${pnlUsd.toFixed(4)}</div>
                          </div>
                        ) : "—"}
                      </TD>
                      <TD>
                        <TokenSparkline tokenId={p.token_id} entryPrice={p.entry_price_usd} />
                      </TD>
                      <TD className="text-right tabular-nums text-[10px] font-mono">
                        <div className="text-green-500">TP1 {fmtPrice(tp1Px)}</div>
                        <div className="text-green-500">TP2 {fmtPrice(tp2Px)}</div>
                        <div className="text-red-500">SL  {fmtPrice(stopPx)}</div>
                      </TD>
                      <TD className="text-center">
                        <div className="flex gap-1 justify-center">
                          <Button size="sm" variant="ghost" disabled={selling}
                                  onClick={() => onSell(p.id, p.symbol, 0.5)}>50%</Button>
                          <Button size="sm" disabled={selling}
                                  onClick={() => onSell(p.id, p.symbol, 1.0)}>All</Button>
                        </div>
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SwitchRow({ label, subtext, isOn, onToggle, disabled, tone, warning }: {
  label: string; subtext: string; isOn: boolean;
  onToggle: () => void; disabled: boolean;
  tone: "good" | "warn" | "muted"; warning?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3 p-3 rounded border bg-muted/5">
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm">{label}</div>
        <div className="text-xs text-muted mt-0.5">{subtext}</div>
        {warning && <div className="text-xs text-yellow-500 mt-1">⚠ {warning}</div>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Badge tone={tone}>{isOn ? "ON" : "OFF"}</Badge>
        <Button size="sm" onClick={onToggle} disabled={disabled}>
          {isOn ? "Turn OFF" : "Turn ON"}
        </Button>
      </div>
    </div>
  );
}

function ConfigItem({ label, value, tone }: {
  label: string; value: string; tone?: "good" | "bad" | "info";
}) {
  const colors = { good: "text-green-500", bad: "text-red-500", info: "" };
  return (
    <div>
      <div className="text-xs text-muted">{label}</div>
      <div className={`font-semibold tabular-nums ${colors[tone ?? "info"]}`}>{value}</div>
    </div>
  );
}
